import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const TURNOS = ["8:00-11:00", "11:00-14:00", "14:00-17:00", "17:00-20:00"];

export default function Guardias() {
  const { canManage } = useAuth();
  const [guardias, setGuardias] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [viewMode, setViewMode] = useState('upcoming');
  const [fecha, setFecha] = useState(null);
  const [turno, setTurno] = useState('');
  const [vendedorId, setVendedorId] = useState('');

  const fetchData = async () => {
    try {
      const [guardiasData, vendorsData] = await Promise.all([api.getGuardias(viewMode === 'upcoming'), canManage ? api.getVendors() : Promise.resolve([])]);
      setGuardias(guardiasData); setVendors(vendorsData);
    } catch (error) { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, canManage]);

  const handleCreate = async () => {
    if (!fecha || !turno || !vendedorId) { toast.error('Completa todos los campos'); return; }
    try {
      await api.createGuardia({ fecha: format(fecha, 'yyyy-MM-dd'), turno, vendedor_id: vendedorId });
      toast.success('Guardia asignada'); setShowCreateDialog(false); setFecha(null); setTurno(''); setVendedorId(''); fetchData();
    } catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al asignar guardia'); }
  };

  const handleDelete = async (guardiaId) => {
    if (!window.confirm('Eliminar esta guardia?')) return;
    try { await api.deleteGuardia(guardiaId); toast.success('Guardia eliminada'); fetchData(); } catch (error) { toast.error('Error al eliminar guardia'); }
  };

  const groupedGuardias = guardias.reduce((acc, guardia) => { const date = guardia.fecha; if (!acc[date]) acc[date] = {}; acc[date][guardia.turno] = guardia; return acc; }, {});
  const sortedDates = Object.keys(groupedGuardias).sort();

  return (
    <div className="space-y-6" data-testid="guardias">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Guardias de Mostrador</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Asignacion de turnos en mostrador</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="segmented-control">
            <button onClick={() => setViewMode('upcoming')} className={`segmented-button ${viewMode === 'upcoming' ? 'active' : ''}`} data-testid="view-upcoming">Proximas</button>
            <button onClick={() => setViewMode('all')} className={`segmented-button ${viewMode === 'all' ? 'active' : ''}`} data-testid="view-all">Historial</button>
          </div>
          {canManage && (
            <button onClick={() => setShowCreateDialog(true)} className="btn-primary flex items-center gap-2" data-testid="create-guardia-btn"><Plus size={16} /> Asignar</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="text-gray-500 text-[13px]">Cargando...</div></div>
      ) : sortedDates.length === 0 ? (
        <div className="mac-card p-8 text-center"><p className="text-gray-500">No hay guardias {viewMode === 'upcoming' ? 'programadas' : 'registradas'}</p></div>
      ) : (
        <div className="mac-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-header">Fecha</th>{TURNOS.map(t => (<th key={t} className="table-header">{t}</th>))}</tr></thead>
              <tbody>
                {sortedDates.map(date => (
                  <tr key={date} className="table-row">
                    <td className="table-cell font-medium text-[#0A0A0A]">{new Date(date + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                    {TURNOS.map(t => {
                      const guardia = groupedGuardias[date][t];
                      return (
                        <td key={t} className="table-cell">
                          {guardia ? (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[12px] text-gray-700">{guardia.vendedor_nombre}</span>
                              {canManage && (<button onClick={() => handleDelete(guardia.id)} className="text-gray-400 hover:text-[#EF4444]" data-testid={`delete-guardia-${guardia.id}`}><Trash2 size={14} /></button>)}
                            </div>
                          ) : (<span className="text-gray-300">-</span>)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">Asignar Guardia</DialogTitle></DialogHeader>
          <div className="p-5 space-y-4">
            <div>
              <label className="form-label">Fecha</label>
              <Popover><PopoverTrigger asChild><button type="button" className="form-input flex items-center justify-between text-left" data-testid="guardia-fecha">{fecha ? format(fecha, 'dd/MM/yyyy') : 'Seleccionar fecha'}<CalendarDays size={18} className="text-gray-400" /></button></PopoverTrigger>
                <PopoverContent className="w-auto p-0 mac-popover" align="start"><Calendar mode="single" selected={fecha} onSelect={setFecha} locale={es} initialFocus /></PopoverContent></Popover>
            </div>
            <div><label className="form-label">Turno</label><select value={turno} onChange={(e) => setTurno(e.target.value)} className="form-input" data-testid="guardia-turno"><option value="">Seleccionar turno</option>{TURNOS.map(t => (<option key={t} value={t}>{t}</option>))}</select></div>
            <div><label className="form-label">Vendedor</label><select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)} className="form-input" data-testid="guardia-vendedor"><option value="">Seleccionar vendedor</option>{vendors.map(v => (<option key={v.id} value={v.id}>{v.nombre}</option>))}</select></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowCreateDialog(false)} className="btn-secondary">Cancelar</button>
              <button onClick={handleCreate} className="btn-primary" data-testid="save-guardia-btn">Asignar</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
